import { useEffect, useRef, useState } from 'preact/hooks';
import { useLocale, type LocaleCode, type LocaleManifestEntry } from '../i18n';

/**
 * Public-chrome language picker. A translate icon + the current locale's native name + a chevron
 * that rotates when the dropdown opens. Reads the manifest from <c>useLocale()</c> and lists
 * every locale, sorted by nativeName via <c>localeCompare</c> (so 한국어 sorts after the Latin
 * block, matching reading expectations).
 *
 * Clicking a row navigates to the same canonical page under the target locale's URL prefix via
 * a full-page load. The full reload is intentional (matches <c>LocaleContextValue.switchLocale</c>):
 * the active locale lives in the URL, so a navigation is the right vector to pick up the new
 * route, fresh i18n catalog, and locale-aware API fetches.
 *
 * <para><b>Behaviour</b>: outside-click and Escape close the menu. Middle-click or Cmd/Ctrl-click
 * preserves the anchor's default behaviour (new tab); only a plain left-click triggers the
 * managed <c>switchLocale</c> navigation.</para>
 *
 * Hidden below md, where the navbar hamburger takes over (see EmporiumNav) and
 * the same locale list renders inline via the `mobile` variant below.
 */
export function LocaleSwitcher({ mobile = false }: { mobile?: boolean } = {}) {
    const { locale, manifest, hrefForLocale, switchLocale } = useLocale();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    const current = manifest.locales.find((l) => l.code === locale);
    const sortedLocales = [...manifest.locales].sort((a, b) =>
        a.nativeName.localeCompare(b.nativeName),
    );

    // Mount the outside-click + Escape listeners only while open so we don't pay the listener
    // cost on every page that renders the navbar.
    useEffect(() => {
        if (mobile || !open) return;
        const onDown = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [mobile, open]);

    if (mobile) {
        return (
            <LocaleOptions
                locales={sortedLocales}
                current={locale}
                hrefForLocale={hrefForLocale}
                switchLocale={switchLocale}
                listClass="flex flex-col gap-1"
            />
        );
    }

    return (
        <div ref={wrapRef} class="relative hidden md:block">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={current?.name ?? 'Language'}
                class="inline-flex items-center gap-1.5 rounded-full border border-transparent px-2 py-1 text-[12.5px] text-fg-muted transition-colors hover:border-rule hover:bg-surface-raised hover:text-fg-primary"
            >
                {/* Translate icon - Lucide `languages`, stroke-only, inherits currentColor. */}
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                >
                    <path d="m5 8 6 6" />
                    <path d="m4 14 6-6 2-3" />
                    <path d="M2 5h12" />
                    <path d="M7 2h1" />
                    <path d="m22 22-5-10-5 10" />
                    <path d="M14 18h6" />
                </svg>
                <span>{current?.nativeName ?? locale}</span>
                <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                    class={`transition-transform ${open ? 'rotate-180' : ''}`}
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {open && (
                <LocaleOptions
                    locales={sortedLocales}
                    current={locale}
                    hrefForLocale={hrefForLocale}
                    switchLocale={switchLocale}
                    onSelect={() => setOpen(false)}
                    listClass="absolute right-0 top-full z-50 mt-1 min-w-[200px] overflow-hidden rounded-md border border-rule bg-surface-raised shadow-[0_12px_32px_-12px_rgba(0,0,0,0.55)]"
                />
            )}
        </div>
    );
}

/**
 * The locale list itself, shared by the desktop dropdown and the mobile
 * drawer variant - only the wrapper class (and whether selecting closes a
 * dropdown) differs between the two.
 */
function LocaleOptions({
    locales,
    current,
    hrefForLocale,
    switchLocale,
    onSelect,
    listClass,
}: {
    locales: LocaleManifestEntry[];
    current: LocaleCode;
    hrefForLocale: (code: string) => string;
    switchLocale: (code: string) => void;
    onSelect?: () => void;
    listClass: string;
}) {
    return (
        <ul role="listbox" aria-label="Language" class={listClass}>
            {locales.map((loc) => {
                const active = loc.code === current;
                return (
                    <li key={loc.code}>
                        <a
                            href={hrefForLocale(loc.code)}
                            hrefLang={loc.code}
                            aria-current={active ? 'page' : undefined}
                            title={loc.name}
                            onClick={(e) => {
                                const me = e as unknown as MouseEvent;
                                if (
                                    me.button !== 0 ||
                                    me.metaKey ||
                                    me.ctrlKey ||
                                    me.shiftKey ||
                                    me.altKey
                                )
                                    return;
                                e.preventDefault();
                                onSelect?.();
                                if (active) return;
                                switchLocale(loc.code);
                            }}
                            class={`flex items-center justify-between gap-3 rounded-md px-3 py-2 text-[13px] transition-colors ${
                                active
                                    ? 'bg-accent-glow text-accent'
                                    : 'text-fg-primary hover:bg-surface-base hover:text-accent'
                            }`}
                        >
                            <span>{loc.nativeName}</span>
                            <span class="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted">
                                {loc.code}
                            </span>
                        </a>
                    </li>
                );
            })}
        </ul>
    );
}
